import { apiRequest } from './standard-api';
import type {
  Industry,
  CreateIndustryDTO,
  UpdateIndustryDTO,
  IndustriesQueryParams,
  IndustriesListResponse,
  IndustryResponse,
} from '@/types/industries';

export type { Industry, IndustriesQueryParams };

const BASE_PATH = '/api/v1/industries';

/**
 * List industries with pagination and filters
 */
export async function listIndustries(
  params?: IndustriesQueryParams
): Promise<IndustriesListResponse> {
  const queryParams = new URLSearchParams();

  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.isActive) queryParams.append('isActive', params.isActive);
  if (params?.search) queryParams.append('search', params.search);
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

  const url = `${BASE_PATH}${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await apiRequest<Industry[]>(url, { method: 'GET' });
  return {
    success: true,
    data: response.data,
    meta: response.meta.pagination as any
  };
}

/**
 * Get industry by ID
 */
export async function getIndustryById(id: string): Promise<IndustryResponse> {
  const response = await apiRequest<Industry>(`${BASE_PATH}/${id}`, { method: 'GET' });
  return {
    success: true,
    data: response.data
  };
}

/**
 * Get industry by code
 */
export async function getIndustryByCode(code: string): Promise<IndustryResponse> {
  const response = await apiRequest<Industry>(`${BASE_PATH}/code/${code}`, { method: 'GET' });
  return {
    success: true,
    data: response.data
  };
}

/**
 * Create industry (Admin only)
 */
export async function createIndustry(data: CreateIndustryDTO): Promise<IndustryResponse> {
  const response = await apiRequest<Industry>(BASE_PATH, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return {
    success: true,
    data: response.data
  };
}

/**
 * Update industry (Admin only)
 */
export async function updateIndustry(
  id: string,
  data: UpdateIndustryDTO
): Promise<IndustryResponse> {
  const response = await apiRequest<Industry>(`${BASE_PATH}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return {
    success: true,
    data: response.data
  };
}

/**
 * Delete industry (Admin only)
 */
export async function deleteIndustry(id: string): Promise<{ success: boolean; data: { message: string } }> {
  const response = await apiRequest<{ message: string }>(`${BASE_PATH}/${id}`, { method: 'DELETE' });
  return {
    success: true,
    data: response.data
  };
}

/**
 * Activate industry (Admin only)
 */
export async function activateIndustry(id: string): Promise<IndustryResponse> {
  const response = await apiRequest<Industry>(`${BASE_PATH}/${id}/activate`, { method: 'PATCH' });
  return {
    success: true,
    data: response.data
  };
}

/**
 * Deactivate industry (Admin only)
 */
export async function deactivateIndustry(id: string): Promise<IndustryResponse> {
  const response = await apiRequest<Industry>(`${BASE_PATH}/${id}/deactivate`, { method: 'PATCH' });
  return {
    success: true,
    data: response.data
  };
}

/**
 * Get active industries only (for selectors)
 */
export async function getActiveIndustries(): Promise<Industry[]> {
  const response = await listIndustries({
    isActive: 'true',
    sortBy: 'sortOrder',
    sortOrder: 'asc',
    limit: 100, // Get all active industries
  });
  return response.data;
}

/**
 * PROJ-038: Bulk delete multiple industries at once
 */
export interface BulkDeleteResponse {
  deleted: number;
  failed: string[];
  total: number;
}

export async function bulkDeleteIndustries(ids: string[]): Promise<BulkDeleteResponse> {
  const response = await apiRequest<BulkDeleteResponse>(`${BASE_PATH}/bulk`, {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
  return response.data;
}
